import * as PopoverPrimitive from '@radix-ui/react-popover';
import type * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * A non-modal panel anchored to a trigger — the header bell, and anything else
 * that needs a menu too rich for a dropdown.
 *
 * Radix, not a hand-rolled absolute div, for the parts that are easy to get
 * wrong: focus moves into the panel and returns to the trigger on close,
 * Escape and outside-click both dismiss, and collision detection keeps the
 * panel on screen on a phone. `modal` is left off so the page behind stays
 * scrollable and screen readers do not treat a notification list as a dialog.
 */
export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverClose = PopoverPrimitive.Close;

export function PopoverContent({
  className,
  align = 'end',
  sideOffset = 8,
  ...props
}: React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        // `--radix-popover-content-available-height` is what keeps a long list
        // from running off the bottom of a short window.
        className={cn(
          'z-50 w-[calc(100vw-2rem)] max-w-sm overflow-hidden rounded-[var(--radius-card)]',
          'border border-line bg-surface shadow-xl outline-none',
          'max-h-[min(32rem,var(--radix-popover-content-available-height))]',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}
